import { GhostButton, GhostButtonProps } from '@mgh-app/component-library';
import { MainLayoutContext } from 'layouts/MainLayout';
import { useAppNavigation } from 'navigation/useAppNavigation';
import { useCallback, useContext, useEffect } from 'react';
import { Outlet, useLocation, useParams } from 'react-router';
import { selectRoomById } from 'services/RoomService';
import { useGetThingInfoQuery } from 'services/ThingsService';
import { useAppSelector } from 'store';
import {
  backgroundColor,
  boxShadow,
  combineCls,
  display,
  flexWrap,
  fontSize,
  gap,
  gridTemplateRows,
  justifyContent,
  margin,
  textColor,
  twCls,
} from 'style';

type ThermoButtonProps = GhostButtonProps & {
  active?: boolean;
};

const ThermoButton = ({ active, ...props }: ThermoButtonProps) => (
  <GhostButton
    size="small"
    className={combineCls(
      twCls(fontSize('text-sm'), active ? textColor('text-blue-100') : textColor('text-grey-900')),
      active && twCls(backgroundColor('bg-white'), boxShadow('shadow-lg')),
    )}
    {...props}
  />
);

export const Temperature = (): JSX.Element => {
  const { setTitle, showNavigationButtons, navigateBackAction } = useContext(MainLayoutContext);
  const { roomId = '', temperatureThingId = '' } = useParams();
  const location = useLocation();
  const {
    navigateTemperature,
    navigateTemperatureAwayMode,
    navigateTemperatureSchedule,
    navigateRoom,
    navigateBack,
  } = useAppNavigation();
  const room = useAppSelector((state) => selectRoomById(state, roomId));
  // const thing = useAppSelector((state) => selectThing(state, roomId, thingId)); // TODO: thing is cleared from the store after 60 secs
  const { data: thing } = useGetThingInfoQuery({ roomId, thingId: temperatureThingId });

  useEffect(() => {
    room?.nickName && setTitle(room?.nickName);
    showNavigationButtons(true);
  }, [room, room?.nickName, setTitle, showNavigationButtons]);

  const navigateBackToRoom = useCallback(() => {
    navigateRoom(roomId);
  }, [navigateRoom, roomId]);

  useEffect(() => {
    room?.nickName && setTitle(room?.nickName);
    showNavigationButtons(true);
    navigateBackAction(() => navigateBackToRoom);

    return () => navigateBackAction(() => navigateBack);
  }, [
    navigateBackAction,
    navigateBack,
    room,
    room?.nickName,
    setTitle,
    showNavigationButtons,
    navigateBackToRoom,
  ]);

  return (
    <div className={twCls(margin('mt-6'), display('grid'), gridTemplateRows('grid-rows-min-fc'))}>
      <h2 className={twCls(display('flex'), justifyContent('justify-center'))}>{thing?.name}</h2>
      <div className={twCls(display('grid'), gridTemplateRows('grid-rows-min-fc'))}>
        <div
          className={twCls(
            margin('mt-4'),
            display('flex'),
            flexWrap('flex-wrap'),
            gap('gap-x-4', 'gap-y-1'),
            justifyContent('justify-center'),
          )}
        >
          <ThermoButton
            active={location.pathname.includes('/thermostat')}
            onClick={() => navigateTemperature(roomId, temperatureThingId)}
          >
            Thermostat
          </ThermoButton>
          <ThermoButton
            active={location.pathname.includes('/schedule')}
            onClick={() => navigateTemperatureSchedule(roomId, temperatureThingId)}
          >
            Schedule
          </ThermoButton>
          <ThermoButton
            active={location.pathname.includes('/away')}
            onClick={() => navigateTemperatureAwayMode(roomId, temperatureThingId)}
          >
            Set Away
          </ThermoButton>
        </div>
        <Outlet />
      </div>
    </div>
  );
};
